import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import ProfileProgressBar from '../components/ProfileProgressBar';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { useProgressWithAutoRefresh } from '../../../shared/contexts/ProgressContext';
import { useStepRedirect, skipNextStepRedirect } from '../../../hooks/useStepRedirect';
import { FaCheck } from "react-icons/fa6";
import { FaExclamationTriangle } from "react-icons/fa";
import { RegistrationApiService } from '../api/RegistrationApiService';
import SignaturePad from '../../../shared/components/ui/SignaturePad';

interface AgreeTermsScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

interface PledgeTerms {
  id: string;
  textAr: string;
  textEn: string;
  checked: boolean;
  required: boolean;
}

const AgreeTermsScreen: React.FC<AgreeTermsScreenProps> = ({ onNext, onBack, onError }) => {
  useStepRedirect(); // Use step redirect hook for navigation

  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [checkingProgress, setCheckingProgress] = useState(true);
  const [canProceed, setCanProceed] = useState(true);
  const [progressWarning, setProgressWarning] = useState<string | null>(null);
  const hasLoadedData = React.useRef(false);
  const registrationApi = new RegistrationApiService();

  const { progress, doctorInfo: progressDoctorInfo } = useProgressWithAutoRefresh();

  // Official syndicate pledge list (updated 2026-07). The Arabic wording is
  // the authoritative text supplied by the syndicate — do not "correct" it.
  // Item medical_supervision is shown to the relevant specialists only (the
  // filter below), matching its "( للاختصاصيين )" note.
  const [pledgeTerms, setPledgeTerms] = useState<PledgeTerms[]>([
    {
      id: 'clinic_transfer',
      textAr: 'عدم تحويل العيادة الى مركز او وضع اسم تجاري مستقبلاً.',
      textEn: 'I pledge not to convert the clinic into a center or adopt a commercial name in the future.',
      checked: false,
      required: true
    },
    {
      id: 'medical_supervision',
      textAr: 'عدم الجمع بين الاشراف الطبي على مركز تجميلي وفتح عيادة في مكان آخر ( للاختصاصيين ).',
      textEn: 'I pledge not to combine medical supervision of a cosmetic center with opening a clinic in another location (for specialists).',
      checked: false,
      required: true
    },
    {
      id: 'no_interventions',
      textAr: 'عدم القيام بأي تداخل ليس من اختصاصي داخل العيادة ولا اسمح للكادر الوسطي بالعمل في العيادة بدون اشراف طبي مني.',
      textEn: 'I pledge not to perform any intervention outside my specialty inside the clinic, and not to allow mid-level staff to work in the clinic without my medical supervision.',
      checked: false,
      required: true
    },
    {
      id: 'building_history',
      textAr: 'ان البناية لم تكن عيادة او مركز تجميل مخالف سابقاً وفى حال ثبوت مخالفتها سابقاً على الطبيب صاحب الطلب اكمال كافة الاجراءات القانونية المطلوبه.',
      textEn: 'The building was not previously a violating clinic or cosmetic center; if a prior violation is proven, the applicant doctor must complete all required legal procedures.',
      checked: false,
      required: true
    },
    {
      id: 'hair_transplant',
      textAr: 'لا يحق للاطباء ممارسي الجلدية والجراحة التجميلية واختصاصيي وممارسي طب الاسرة فتح قسم زراعة الشعر أو ممارسة العمل في هذا الحقل.',
      textEn: 'Dermatology and cosmetic surgery practitioners, and family medicine specialists and practitioners, may not open a hair transplant section or practice in this field.',
      checked: false,
      required: true
    },
    {
      id: 'signboard_name',
      textAr: 'يكتب الاسم الثلاثي للطبيب على جميع اللوحات التعريفية مع رمز التحقق الألكتروني (QR) ورقم التسجيل وتأريخه على الوصفة الطبية.',
      textEn: 'The doctor\'s full (three-part) name must appear on all identification signboards, with the electronic verification (QR) code, and the registration number and its date on the medical prescription.',
      checked: false,
      required: true
    },
    {
      id: 'staff_responsibility',
      textAr: 'يتحمل الطبيب مسؤولية جميع العاملين في العيادة سواء كانوا اجانب أو عراقيين من ناحية موافقات العمل الإدارية والقانونية.',
      textEn: 'The doctor bears responsibility for all clinic staff, whether foreign or Iraqi, regarding administrative and legal work approvals.',
      checked: false,
      required: true
    },
    {
      id: 'single_clinic',
      textAr: 'عدم فتح أكثر من عيادة واحدة في عموم العراق.',
      textEn: 'I pledge not to open more than one clinic anywhere in Iraq.',
      checked: false,
      required: true
    },
    {
      id: 'no_legal_referral',
      textAr: 'أتعهد أن لايكون المكان المراد فتح عيادة به محالاً إلى الجهات القانونية والمحاكم المختصة.',
      textEn: 'I pledge that the place intended for the clinic is not referred to the legal authorities or the competent courts.',
      checked: false,
      required: true
    },
    {
      id: 'media_promotion',
      textAr: 'أتعهد بالألتزام بالترويج الأعلامي المقر ضمن الضوابط والتعليمات النقابية, وأقر بإني أستلمت نسخة من تلك التعليمات بقصد العمل بها.',
      textEn: 'I pledge to comply with the approved media promotion rules within the syndicate\'s regulations and instructions, and I acknowledge receiving a copy of those instructions in order to abide by them.',
      checked: false,
      required: true
    },
    {
      id: 'no_center_supervision',
      textAr: 'أتعهد بعدم الإشراف على مركز طبي بالإضافة إلى عيادتي الخاصة وعدم الجمع بين الاثنين في وقت واحد.',
      textEn: 'I pledge not to supervise a medical center in addition to my own private clinic, and not to combine the two at the same time.',
      checked: false,
      required: true
    }
  ]);

  // Check doctor's registration progress when progress data is available
  useEffect(() => {
    // Wait for progress data to load
    if (!progress || hasLoadedData.current) {
      return;
    }

    hasLoadedData.current = true;
    setCheckingProgress(true);

    try {
      // Filter pledge terms based on doctor's specialty
      // Note: practition may not be in the type definition but can exist in API response
      const specialty = (progressDoctorInfo as any)?.practition?.name;

      // Define specialties that should show the medical supervision pledge
      const specialtiesRequiringMedicalSupervisionPledge = [
        'General Surgery (GP - Board Certified - Higher Diploma - FRCS)',
        'Plastic and Reconstructive Surgery (GP - Board Certified)',
        'Dermatology (GP - Board Certified)',
        'Family Medicine (GP - Board Certified - Higher Diploma)'
      ];

      // If doctor's specialty is NOT in the list, exclude the medical supervision pledge
      if (!specialty || !specialtiesRequiringMedicalSupervisionPledge.includes(specialty)) {
        setPledgeTerms(prev => prev.filter(term => term.id !== 'medical_supervision'));
      }

      // Check active registration request stage
      const registrationRequest = progress.active_requests?.find(
        (r: any) => r.request_type === 'registration'
      );
      const currentStage = registrationRequest?.current_stage;

      // Block if they've already completed this step (payment stage or beyond)
      if (currentStage === 'payment' || currentStage === 'inspection' || currentStage === 'approval') {
        setCanProceed(false);
        setProgressWarning(t('terms_already_agreed'));
      } else {
        // Doctor can proceed
        setCanProceed(true);
        setProgressWarning(null);
      }
    } catch (error) {
      console.error('Error checking registration progress:', error);
      // Still allow proceeding in case of error
      setCanProceed(true);
    } finally {
      setCheckingProgress(false);
    }
  }, [progress, progressDoctorInfo, t]);

  const handleTermChange = (termId: string, checked: boolean) => {
    setPledgeTerms(prev => 
      prev.map(term => 
        term.id === termId ? { ...term, checked } : term
      )
    );
  };

  const allRequiredTermsChecked = pledgeTerms.filter(term => term.required).every(term => term.checked);

  const handleSubmit = async () => {
    if (!allRequiredTermsChecked) {
      onError(t('agree_to_all_pledges'));
      return;
    }
    // Accepting the pledges requires a hand-drawn signature: open the pad;
    // the agreement is only registered after the signature uploads.
    setShowSignaturePad(true);
  };

  const handleSignatureConfirm = async (blob: Blob) => {
    setLoading(true);

    try {
      const regRequestId = progress?.active_requests?.find(
        (r: any) => r.request_type === 'registration'
      )?.request_id;
      if (!regRequestId) {
        onError(t('no_active_registration') || 'No active registration found.');
        return;
      }

      // 1. Upload the signature image to the doctor's documents
      const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });
      const uploadResult = await registrationApi.uploadDoctorDocument(signatureFile, 'signature');
      if (!uploadResult.isSuccess) {
        onError(uploadResult.error || t('signature_upload_failed'));
        return; // keep the pad open so the doctor can retry
      }

      console.log('✅ Signature uploaded, registering agreement...');

      // 2. Register that terms have been agreed to
      // This will automatically advance registration_progress to 'pay'
      const result = await registrationApi.agreeToTerms(true, regRequestId);

      if (!result.isSuccess) {
        onError(result.error || t('failed_register_agreement'));
        return;
      }

      console.log('✅ Backend updated, proceeding to payment...');

      // Navigate directly to payment, skip redirect check on destination
      skipNextStepRedirect();
      navigate('/payment');

    } catch (error) {
      console.error('❌ Terms submission error:', error);
      onError(error instanceof Error ? error.message : t('failed_proceed_payment'));
    } finally {
      setLoading(false);
    }
  };

  const actionButtons = (
    <div className="flex gap-3 w-full">
      <button
        onClick={onBack}
        disabled={loading || checkingProgress}
        className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
      >
        {t('back') || 'Back'}
      </button>
      <button
        onClick={handleSubmit}
        disabled={loading || !allRequiredTermsChecked || !canProceed || checkingProgress}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading || !allRequiredTermsChecked || !canProceed || checkingProgress
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
        }`}
        style={{ backgroundColor: '#2E5D7D' }}
      >
        {loading || checkingProgress ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>{checkingProgress ? t('checking') : t('loading')}</span>
          </div>
        ) : (
          t('register')
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={onBack}
      showBackButton={true}
      title={t('open_clinic')}
      subtitle={t('pledge')}
      actions={actionButtons}
      progressValue={85}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Profile Progress Bar */}
      <ProfileProgressBar />
      
      <div className="space-y-6">
        {/* Pledge Terms Header */}
        <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {t('i_pledge_following')}
          </h3>
        </div>

        {/* Pledge Terms List */}
        <div className="space-y-4">
          {pledgeTerms.map((term) => (
            <div
              key={term.id}
              className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-sm ${
                term.checked
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              onClick={() => handleTermChange(term.id, !term.checked)}
            >
              <div className="flex-shrink-0 mt-1">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                    term.checked
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {term.checked && (
                    <FaCheck className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
              
              <div className="flex-1">
                <p className={`text-gray-800 text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? term.textAr : term.textEn}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Registration Progress Warning */}
        {progressWarning && !canProceed && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className={`text-red-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {progressWarning}
              </p>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        {!allRequiredTermsChecked && canProceed && !checkingProgress && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <p className={`text-amber-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('must_agree_all_pledges')}
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {allRequiredTermsChecked && canProceed && !checkingProgress && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <FaCheck className="w-3 h-3 text-white" />
              </div>
              <p className={`text-green-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('all_pledges_accepted')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Signature modal — accepting the pledges requires a hand-drawn
          signature, uploaded to the doctor's documents before continuing. */}
      {showSignaturePad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <h2 className={`text-lg font-bold text-gray-900 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('signature_title')}
            </h2>
            <p className={`text-sm text-gray-600 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('signature_instruction')}
            </p>
            <SignaturePad
              onConfirm={handleSignatureConfirm}
              onCancel={() => setShowSignaturePad(false)}
              confirmLabel={t('confirm_signature')}
              clearLabel={t('clear_signature')}
              cancelLabel={t('back') || 'Back'}
              loading={loading}
            />
          </div>
        </div>
      )}
    </ResponsiveFormLayout>
  );
};

export default AgreeTermsScreen;